import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon, PhoneArrowUpRightIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { leadService } from '../../services/leadService';
import { agentService } from '../../services/agentService';
import { campaignService } from '../../services/campaignService';
import { useAuth } from '../../contexts/AuthContext';
import { useWebRTC } from '../../contexts/WebRTCContext';
import { Lead, LeadNote, LeadSubStage, LeadTimelineEvent, CallAttempt, LeadStatus, LeadFollowUp } from '../../types/lead';
import { Campaign } from '../../types/campaign';
import LoadingSpinner from '../UI/LoadingSpinner';
import LeadTimeline from './LeadTimeline';
import LeadNotesList from './LeadNotesList';
import LeadCallHistory from './LeadCallHistory';
import LeadPipelinePanel from './LeadPipelinePanel';
import AgentTagPicker from './AgentTagPicker';
import FollowUpDateEditor from './FollowUpDateEditor';
import LeadEditModal from './LeadEditModal';
import Button from '../UI/Button';
import { cn } from '../../utils/cn';
import { isViewResultsDialableCampaign } from '../../hooks/useDialLead';
import { formatLeadCarrier, formatLeadLineType } from '../../utils/leadLookupDisplay';

type SidebarTab = 'pipeline' | 'timeline' | 'notes' | 'calls';

const PANEL_TRANSITION_MS = 320;

interface LeadDetailSidebarProps {
  leadId: string | null;
  onClose: () => void;
  onLeadUpdated?: () => void;
  /** Call + inline edit in sidebar — main nav View Results only */
  enableContactActions?: boolean;
  /** When dialing from View Results, use call-history-only disposition popup */
  callDispositionMode?: 'results_callback' | 'main_dial';
}

const SIDEBAR_SELECT =
  'mt-1 block h-9 w-full rounded-lg border border-border bg-background px-3 py-1.5 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-50';

const MAIN_STATUS_OPTIONS: { value: LeadStatus; label: string }[] = [
  { value: 'interested', label: 'Interested' },
  { value: 'not_interested', label: 'Not Interested' },
  { value: 'follow_up', label: 'Follow Up' },
  { value: 'app_out', label: 'App Out' },
  { value: 'app_in', label: 'App In' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'contract', label: 'Contract' },
  { value: 'funded', label: 'Funded' },
  { value: 'unqualified', label: 'Unqualified' },
  { value: 'no_answer', label: 'No Answer' },
];

function getLatestCallTimestamp(call: CallAttempt): string | undefined {
  const ts = call.finished_at || call.started_at || call.created_at;
  return ts ? String(ts) : undefined;
}

const LeadDetailSidebar: React.FC<LeadDetailSidebarProps> = ({
  leadId,
  onClose,
  onLeadUpdated,
  enableContactActions = false,
  callDispositionMode = 'results_callback',
}) => {
  const { user, isAdmin } = useAuth();
  const { status: webrtcStatus, makeOutboundCall } = useWebRTC();
  const [lead, setLead] = useState<Lead | null>(null);
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [timeline, setTimeline] = useState<LeadTimelineEvent[]>([]);
  const [notes, setNotes] = useState<LeadNote[]>([]);
  const [calls, setCalls] = useState<CallAttempt[]>([]);
  const [followUps, setFollowUps] = useState<LeadFollowUp[]>([]);
  const [subStages, setSubStages] = useState<LeadSubStage[]>([]);
  const [activeTab, setActiveTab] = useState<SidebarTab>('timeline');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dialing, setDialing] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [panelVisible, setPanelVisible] = useState(false);
  const [displayLeadId, setDisplayLeadId] = useState<string | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const activeLeadId = leadId ?? displayLeadId;

  const loadLead = useCallback(async () => {
    if (!activeLeadId) return;
    setLoading(true);
    try {
      const [leadData, timelineData, notesData, callsData, followUpsData] = await Promise.all([
        leadService.getLead(activeLeadId),
        leadService.getLeadTimeline(activeLeadId),
        leadService.getLeadNotes(activeLeadId),
        leadService.getCallAttempts(activeLeadId),
        leadService.getLeadFollowUps(activeLeadId),
      ]);
      setLead(leadData);
      setTimeline(timelineData);
      setNotes(notesData);
      setCalls(callsData);
      setFollowUps(followUpsData);
      if (leadData.campaignId) {
        const stages = await leadService.getSubStages({
          campaignId: leadData.campaignId,
          mainStatus: leadData.status,
        });
        setSubStages(stages);
        if (enableContactActions) {
          const campaignData = await campaignService.getCampaign(leadData.campaignId).catch(() => null);
          setCampaign(campaignData);
        } else {
          setCampaign(null);
        }
      } else {
        setCampaign(null);
      }
    } catch (err) {
      console.error('Failed to load lead sidebar:', err);
    } finally {
      setLoading(false);
    }
  }, [activeLeadId, enableContactActions]);

  const requestClose = useCallback(() => {
    setPanelVisible(false);
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      onClose();
    }, PANEL_TRANSITION_MS);
  }, [onClose]);

  useEffect(() => {
    if (!leadId) return;

    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }

    setDisplayLeadId(leadId);
    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(() => setPanelVisible(true));
    });
    void loadLead();
    setActiveTab('pipeline');
    return () => cancelAnimationFrame(frame);
  }, [leadId, loadLead]);

  useEffect(() => {
    if (leadId || !displayLeadId) return;

    setPanelVisible(false);
    closeTimerRef.current = setTimeout(() => {
      setDisplayLeadId(null);
      setLead(null);
      closeTimerRef.current = null;
    }, PANEL_TRANSITION_MS);

    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, [leadId, displayLeadId]);

  useEffect(() => {
    if (!panelVisible) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [panelVisible]);

  useEffect(() => {
    if (!displayLeadId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') requestClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [displayLeadId, requestClose]);

  useEffect(
    () => () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    },
    []
  );

  const updateLeadField = async (patch: Parameters<typeof leadService.updateLead>[1]) => {
    if (!activeLeadId) return;
    setSaving(true);
    try {
      const updated = await leadService.updateLead(activeLeadId, patch);
      setLead(updated);
      onLeadUpdated?.();
      if (patch.status && updated.campaignId) {
        const stages = await leadService.getSubStages({
          campaignId: updated.campaignId,
          mainStatus: patch.status,
        });
        setSubStages(stages);
      }
      const [timelineData, followUpsData, refreshedLead] = await Promise.all([
        leadService.getLeadTimeline(activeLeadId),
        leadService.getLeadFollowUps(activeLeadId),
        leadService.getLead(activeLeadId),
      ]);
      setTimeline(timelineData);
      setFollowUps(followUpsData);
      setLead(refreshedLead);
    } finally {
      setSaving(false);
    }
  };

  const handleAddNote = async (body: string) => {
    if (!activeLeadId) return;
    const note = await leadService.addLeadNote(activeLeadId, body);
    setNotes((prev) => [note, ...prev]);
    const timelineData = await leadService.getLeadTimeline(activeLeadId);
    setTimeline(timelineData);
  };

  const handleUpdateNote = async (noteId: string, body: string) => {
    if (!activeLeadId) return;
    const updated = await leadService.updateLeadNote(activeLeadId, noteId, body);
    setNotes((prev) => prev.map((n) => (n.id === noteId ? updated : n)));
    const timelineData = await leadService.getLeadTimeline(activeLeadId);
    setTimeline(timelineData);
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!activeLeadId) return;
    await leadService.deleteLeadNote(activeLeadId, noteId);
    setNotes((prev) => prev.filter((n) => n.id !== noteId));
    const timelineData = await leadService.getLeadTimeline(activeLeadId);
    setTimeline(timelineData);
  };

  const isViewResultsDialable =
    Boolean(lead?.campaignId) &&
    isViewResultsDialableCampaign({
      campaignType: campaign?.campaignType,
    });

  const handleDialLead = async () => {
    if (!lead?.id || !lead.campaignId || !lead.phoneE164) return;
    if (!isViewResultsDialable) {
      toast.error('Voice calling is not available for SMS campaigns.');
      return;
    }
    if (webrtcStatus !== 'ready' && webrtcStatus !== 'in-call') {
      toast.error('WebRTC not connected. Please wait for connection or refresh the page.');
      return;
    }

    setDialing(true);
    try {
      const data = await agentService.prepareDial(lead.id, lead.campaignId, {
        resultsCallback: enableContactActions,
      });
      makeOutboundCall({
        destinationNumber: data.fromNumber,
        callerNumber: data.fromNumber,
        callAttemptId: data.callAttemptId,
        routingAttemptId: data.routingAttemptId,
        routingLegId: data.routingLegId,
        campaignId: data.campaignId,
        leadId: data.leadId,
        leadName: data.leadName,
        leadPhone: data.leadPhone || data.toNumber,
        leadEmail: data.leadEmail || null,
        companyName: data.companyName || null,
        campaignName: data.campaignName || null,
        scriptName: data.scriptName || null,
        scriptContent: data.scriptContent || null,
        scriptStructure: data.scriptStructure || null,
        agentId: data.agentId,
        leadStatus: data.leadStatus || null,
        subStageId: data.subStageId || null,
        dispositionMode: enableContactActions ? callDispositionMode : 'main_dial',
      });
      toast.success(`Dialing ${lead.fullName || lead.phoneE164}...`);
    } catch (error: unknown) {
      const err = (error as { response?: { data?: { error?: string | { message?: string } } }; message?: string })
        ?.response?.data?.error;
      const message =
        typeof err === 'string' ? err : (err as { message?: string })?.message || (error as Error)?.message || 'Failed to dial lead';
      toast.error(message);
    } finally {
      setDialing(false);
    }
  };

  if (!displayLeadId) return null;

  const effectiveLastCall =
    lead?.lastCallAt ?? (calls.length > 0 ? getLatestCallTimestamp(calls[0]) : undefined);

  const panel = (
    <>
      <div
        className={cn(
          'fixed inset-0 z-40 bg-black/50 backdrop-blur-[2px] transition-opacity duration-300 ease-out',
          panelVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
        onClick={requestClose}
        aria-hidden
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="lead-detail-sidebar-title"
        className={cn(
          'fixed top-0 right-0 z-50 flex h-full w-full max-w-lg flex-col border-l border-border bg-background shadow-2xl',
          'transform transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] will-change-transform',
          panelVisible ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 id="lead-detail-sidebar-title" className="font-semibold text-foreground">
            Lead Details
          </h2>
          <button
            type="button"
            onClick={requestClose}
            className="rounded-md p-1 transition-colors hover:bg-accent"
            aria-label="Close sidebar"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {loading && !lead ? (
          <div className="flex-1 flex items-center justify-center">
            <LoadingSpinner />
          </div>
        ) : lead ? (
          <>
            <div className="scroll-area max-h-[42vh] shrink-0 overflow-y-auto border-b border-border px-4 py-3 space-y-3">
              <div>
                <div className="flex items-center justify-between gap-2 min-w-0">
                  <h3 className="text-lg font-semibold text-foreground truncate">{lead.fullName || 'Unknown'}</h3>
                  {enableContactActions && (
                    <Button
                      variant="outlined"
                      size="sm"
                      className="shrink-0"
                      onClick={() => setEditOpen(true)}
                      title="Edit lead"
                      aria-label="Edit lead"
                    >
                      <PencilSquareIcon className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                {(lead.campaignName || lead.campaign_name) && (
                  <p className="text-xs text-primary/80 mt-0.5">{lead.campaignName || lead.campaign_name}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
                <div>
                  <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Phone</p>
                  <div className="flex items-center gap-1.5 min-w-0">
                    <p className="text-foreground truncate flex-1">{lead.phoneE164 || '—'}</p>
                    {enableContactActions && lead.phoneE164 && lead.campaignId && (
                      <button
                        type="button"
                        onClick={() => void handleDialLead()}
                        disabled={dialing || !isViewResultsDialable}
                        title={
                          isViewResultsDialable
                            ? 'Call lead'
                            : 'Voice calling is not available for SMS campaigns'
                        }
                        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-border text-primary hover:bg-accent disabled:cursor-not-allowed disabled:opacity-40"
                        aria-label="Call lead"
                      >
                        <PhoneArrowUpRightIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Email</p>
                  <p className="text-foreground truncate">{lead.email || '—'}</p>
                </div>
                <div>
                  <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Company</p>
                  <p className="text-foreground truncate">{lead.companyName || '—'}</p>
                </div>
                <div>
                  <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Type</p>
                  <p className="text-foreground truncate">{formatLeadLineType(lead.lineType)}</p>
                </div>
                <div>
                  <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Carrier</p>
                  <p className="text-foreground truncate">{formatLeadCarrier(lead.carrierName, lead.carrier)}</p>
                </div>
                <div>
                  <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Last Call</p>
                  <p className="text-foreground truncate">
                    {effectiveLastCall ? new Date(effectiveLastCall).toLocaleString() : '—'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Status</label>
                  <select
                    className={SIDEBAR_SELECT}
                    value={lead.status}
                    disabled={saving}
                    onChange={(e) => updateLeadField({ status: e.target.value as LeadStatus })}
                  >
                    {MAIN_STATUS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Sub-Stage</label>
                  <select
                    className={SIDEBAR_SELECT}
                    value={lead.subStageId || ''}
                    disabled={saving || subStages.length === 0}
                    onChange={(e) => updateLeadField({ subStageId: e.target.value || null })}
                  >
                    <option value="">None</option>
                    {subStages.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <FollowUpDateEditor
                leadId={lead.id}
                leadName={lead.fullName}
                followUpDate={lead.followUpDate}
                status={lead.status}
                onUpdated={() => void loadLead()}
                className="!w-full !max-w-none"
              />

              {lead.assignedAgent && (
                <p className="text-xs text-muted-foreground">
                  Assigned: <span className="text-foreground">{lead.assignedAgent.name}</span>
                </p>
              )}
              <AgentTagPicker
                leadId={lead.id}
                taggedAgents={lead.taggedAgents}
                onUpdated={(taggedAgents) => {
                  setLead((prev) => (prev ? { ...prev, taggedAgents } : prev));
                  onLeadUpdated?.();
                }}
              />
            </div>

            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <div className="flex shrink-0 border-b border-border">
              {(['pipeline', 'timeline', 'notes', 'calls'] as SidebarTab[]).map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setActiveTab(tab)}
                  className={cn(
                    'flex-1 border-b-2 py-2.5 text-sm font-medium capitalize transition-colors duration-200',
                    activeTab === tab
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  )}
                >
                  {tab === 'calls' ? 'Call History' : tab}
                </button>
              ))}
              </div>

            <div className="scroll-area min-h-0 flex-1 overflow-y-auto px-4 py-4 pb-8">
              <div key={activeTab} className="animate-fade-in" style={{ animationDuration: '0.22s' }}>
              {activeTab === 'pipeline' && (
                <LeadPipelinePanel
                  lead={lead}
                  subStages={subStages}
                  followUps={followUps}
                  timeline={timeline}
                  saving={saving}
                  onStatusChange={(status) => void updateLeadField({ status })}
                  onSubStageChange={(subStageId) => void updateLeadField({ subStageId })}
                  onFollowUpUpdated={() => void loadLead()}
                />
              )}
              {activeTab === 'timeline' && <LeadTimeline events={timeline} loading={loading} />}
              {activeTab === 'notes' && (
                <div className="flex min-h-[300px] flex-col">
                  <LeadNotesList
                    notes={notes}
                    loading={loading}
                    currentUserId={user?.id}
                    isAdmin={isAdmin}
                    onAddNote={handleAddNote}
                    onUpdateNote={handleUpdateNote}
                    onDeleteNote={handleDeleteNote}
                  />
                </div>
              )}
              {activeTab === 'calls' && (
                <LeadCallHistory
                  calls={calls}
                  loading={loading}
                  leadName={lead.fullName}
                  isAdmin={isAdmin}
                />
              )}
              </div>
            </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
            Lead not found.
          </div>
        )}
      </aside>

      {lead && enableContactActions && (
        <LeadEditModal
          isOpen={editOpen}
          leadId={lead.id}
          initial={{
            fullName: lead.fullName,
            email: lead.email,
            companyName: lead.companyName,
            followUpDate: lead.followUpDate,
            status: lead.status,
            notes: lead.notes,
          }}
          onClose={() => setEditOpen(false)}
          onSaved={() => {
            setEditOpen(false);
            void loadLead();
            onLeadUpdated?.();
          }}
        />
      )}
    </>
  );

  return createPortal(panel, document.body);
};

export default LeadDetailSidebar;
